/**
 * Validates an entire form based on a given schema and form data.
 *
 * @param {Object} schema - An object defining validation rules for each field.
 *   Each key is a field name, and the value is an array of validator functions.
 *   A validator function should have the signature `(value, allData) => string | null`,
 *   returning an error message string if invalid, or null if valid.
 * @param {Object} data - The form data to validate, where keys correspond to field names.
 *
 * @returns {Object} An object containing:
 *   - valid {boolean}: true if all fields are valid, false otherwise.
 *   - errors {Object}: an object mapping each field to its error message (or null if no error).
 *
 * @example
 * const schema = {
 *   username: [value => value ? null : 'Username required'],
 *   age: [value => value >= 18 ? null : 'Must be at least 18'],
 * };
 * const data = { username: '', age: 16 };
 * const result = validateForm(schema, data);
 * console.log(result.valid); // false
 * console.log(result.errors); // { username: 'Username required', age: 'Must be at least 18' }
 */
export function validateForm(schema, data) {
  const errors = {};

  Object.keys(schema).forEach((field) => {
    const validators = schema[field];

    for (let validate of validators) {
      const error = validate(data[field], data);
      if (error) {
        errors[field] = error;
        break;
      }
    }

    if (!errors[field]) errors[field] = null;
  });

  return {
    valid: Object.values(errors).every((e) => e === null),
    errors,
  };
}

/**
 * Validates a single field based on the given schema and form data.
 *
 * @param {Object} schema - The validation schema for the form.
 * @param {string} field - The name of the field to validate.
 * @param {Object} data - The form data containing the field value.
 *
 * @returns {string|null} The first error message returned by a validator, or null if the field is valid.
 *
 * @example
 * const schema = {
 *   email: [
 *     value => value.includes('@') ? null : 'Invalid email address',
 *   ],
 * };
 * const data = { email: 'example.com' };
 * const error = validateField(schema, 'email', data);
 * console.log(error); // 'Invalid email address'
 */
export function validateField(schema, field, data) {
  const validators = schema[field];

  for (let validate of validators) {
    const error = validate(data[field], data);
    if (error) return error;
  }
  return null;
}
